import React from "react";
import { IconButton, TablePagination } from "@mui/material";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import ViewOrderButton from "../order-actions/ViewOrderButton";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";
import { statusColors } from "src/business-types/grocery/layouts/orders/OrderSidePanal";

interface OrderTableProps {
  data: Order[];
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onViewOrder: (orderId: string) => void;
  viewLoading: boolean;
}

type OrderRow = {
  id: string;
  sno: number;
  orderId: string;
  date: string;
  name: string;
  phone: string;
  staff: string;
  status: React.ReactNode;
  amount: string;
  actions: JSX.Element;
};

const OrderTable: React.FC<OrderTableProps> = ({ data, page, rowsPerPage, totalRecords, onPageChange, onRowsPerPageChange, onViewOrder, viewLoading }) => {
  const { formatDate } = useDateTimeFormatter();

  // ✅ Transform Orders Safely with Status Colors (matching OrderSearch pattern)
  const rows: OrderRow[] = data
    .filter((order) => order?.orders?.length && order.orders[0]?.status !== "deleted")
    .map((order, index) => {
      const txn = order.orders?.[0];
      const customer = order.customerDetails;
      const orderedOn = order.orderDetails?.[0]?.orderedOn?.$date ?? "";

      const { date: orderDate } = splitDateTime(orderedOn);
      const orderPrimaryId = txn?.orderId || order._id?.$oid || `row-${index}`;

      // ✅ Safe staff name extraction
      const getStaffName = () => {
        const staffDetails = txn?.staffDetails;
        if (!staffDetails) return "N/A";
        return [staffDetails.firstName, staffDetails.middleName, staffDetails.lastName].filter(Boolean).join(" ") || "N/A";
      };

      // ✅ Safe customer name extraction
      const getCustomerName = () => {
        if (!customer) return "N/A";
        return [customer.firstName, customer.middleName, customer.lastName].filter(Boolean).join(" ") || "N/A";
      };

      // ✅ Get status color from statusColors (matching OrderSearch pattern)
      const statusColor = statusColors?.[txn?.status] || "bg-gray-100 text-gray-700";

      return {
        id: orderPrimaryId,
        sno: page * rowsPerPage + index + 1,
        orderId: txn?.orderId || "—",
        date: formatDate(orderDate) || "—",
        name: getCustomerName(),
        phone: customer?.phones?.[0]?.phone || "N/A",
        staff: getStaffName(),
        // ✅ Render status as a badge with color (matching OrderSearch pattern)
        status: (
          <span
            className={`px-2 py-1 rounded font-semibold text-xs ${statusColor}`}
            style={{
              display: "inline-block",
              minWidth: "70px",
              textAlign: "center",
            }}
          >
            {txn?.status || "N/A"}
          </span>
        ),
        amount: txn ? Number(txn.finalAmount).toFixed(2) : "0.00",
        actions: (
          <div>
            <span>
              <IconButton disabled={!orderPrimaryId} onClick={() => onViewOrder(orderPrimaryId)} size="small">
                <ViewOrderButton orderId={orderPrimaryId} isLoading={viewLoading} onViewOrder={onViewOrder} />
              </IconButton>
            </span>
          </div>
        ),
      };
    });

  // ✅ Define table headers
  const headCells: HeadCell<OrderRow>[] = [
    { id: "sno", label: "S.No", sorting: false },
    { id: "orderId", label: "Order ID" },
    { id: "date", label: "Date" },
    { id: "name", label: "Customer Name" },
    { id: "phone", label: "Phone Number" },
    { id: "staff", label: "Staff Name" },
    { id: "status", label: "Status" },
    { id: "amount", label: "Amount" },
    { id: "actions", label: "Actions", sorting: false },
  ];

  return (
    <div className="w-full flex-1 overflow-y-auto">
      <CommonTable<OrderRow>
        key={rowsPerPage}
        data={rows}
        headCells={headCells}
        showToolbar={false}
        denseDefault={false}
        title="Orders"
        pageSize={rowsPerPage}
        pagenation={false}
        selectable={false}
        className="w-full min-w-[85vh] max-w-[100vw]"
      />

      <div className="flex justify-end mr-8 mt-2">
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onRowsPerPageChange(newSize);
            onPageChange(0);
          }}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        />
      </div>
    </div>
  );
};

export default OrderTable;
